import React from 'react';

interface JoinCircleFormProps {
  fullName: string;
  email: string;
  phone: string;
  identifierMode: 'email' | 'phone';
  /** When true, the phone/email identifier is bound to the invite and the
   *  recipient can't change it (or switch modes). The server enforces this
   *  too — this prop is just for clarity in the UI. */
  identifierLocked?: boolean;
  submitting: boolean;
  error: string | null;
  circleName: string;
  inviterName: string;
  onFullNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onIdentifierModeChange: (mode: 'email' | 'phone') => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const JoinCircleForm: React.FC<JoinCircleFormProps> = ({
  fullName,
  email,
  phone,
  identifierMode,
  identifierLocked = false,
  submitting,
  error,
  circleName,
  inviterName,
  onFullNameChange,
  onEmailChange,
  onPhoneChange,
  onIdentifierModeChange,
  onSubmit,
}) => {
  const isPhone = identifierMode === 'phone';
  const identifierValue = isPhone ? phone : email;
  const submitDisabled =
    submitting || !fullName.trim() || !identifierValue.trim();

  return (
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
      <div className="text-center mb-6">
        <img src="/kizu-mascot.png" alt="Kizu" className="w-16 h-16 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-gray-800">Join {circleName}</h1>
        <p className="text-gray-600 mt-2">
          <strong>{inviterName}</strong> invited you to join this circle
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => onFullNameChange(e.target.value)}
            placeholder="Enter your full name"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-gray-900 placeholder-gray-400 bg-white"
            required
            autoFocus
          />
        </div>

        {/* Toggle between phone and email — only shown for QR templates
            where the recipient is free to pick. Direct invites are bound
            to the identifier they were sent to and the toggle is hidden. */}
        {!identifierLocked && (
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => onIdentifierModeChange('phone')}
              className={`flex-1 px-3 py-2 rounded-lg border ${
                isPhone
                  ? 'bg-blue-50 border-blue-500 text-blue-700 font-semibold'
                  : 'bg-white border-gray-300 text-gray-600'
              }`}
            >
              📱 Phone
            </button>
            <button
              type="button"
              onClick={() => onIdentifierModeChange('email')}
              className={`flex-1 px-3 py-2 rounded-lg border ${
                !isPhone
                  ? 'bg-blue-50 border-blue-500 text-blue-700 font-semibold'
                  : 'bg-white border-gray-300 text-gray-600'
              }`}
            >
              ✉️ Email
            </button>
          </div>
        )}

        {/* Identifier field. When locked (direct invite), render a non-
            editable display row so the recipient can see exactly which
            number/email they were invited as. The server will reject any
            attempt to submit a different value anyway. */}
        {identifierLocked ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isPhone ? 'Phone Number' : 'Email Address'}
            </label>
            <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-lg text-gray-700 flex items-center justify-between">
              <span>{isPhone ? phone : email}</span>
              <span className="text-xs text-gray-400 ml-2">🔒 Invited</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This invitation was sent to this {isPhone ? 'number' : 'email'} and can only be claimed by the recipient.
            </p>
          </div>
        ) : isPhone ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="+1 (555) 555-5555"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-gray-900 placeholder-gray-400 bg-white"
              required
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-gray-900 placeholder-gray-400 bg-white"
              required
              autoComplete="email"
            />
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={submitDisabled}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Joining...' : 'Join Circle'}
        </button>
      </form>
    </div>
  );
};
